// Directive v-number
// -------------------
// Assainit les saisies numériques sur n'importe quel champ
// Agissant directement sur le <input> réellement rendu.
const KEYPRESS_HANDLER = "__vNumberKeypress__";
const INPUT_HANDLER = "__vNumberInput__";
const TYPED_VALUE = "__vNumberTypedValue__";
const TYPED_CARET = "__vNumberTypedCaret__";

// Types d'input qui supportent setSelectionRange
const SELECTABLE_TYPES = new Set(["text", "search", "url", "tel", "password"]);

// Récupère le <input> : soit l'élément lui-même, soit le premier <input> enfant
const getInput = (el) =>
  el?.tagName === "INPUT" ? el : (el?.querySelector("input") ?? null);

export const NUMERIC_RE = /^-?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;

// Nettoie une chaîne en une chaîne numérique valide.
const sanitize = (raw) => {
  const str = String(raw ?? "")
    .replace(",", ".")
    .trim();
  if (NUMERIC_RE.test(str)) return str;
  let value = str;
  // ne garder que chiffres, point et tiret
  value = value.replace(/[^\d.-]/g, "");
  // un seul "-", uniquement en tête
  const isNegative = value.startsWith("-");
  value = value.replace(/-/g, "");
  // un seul "."
  const firstDot = value.indexOf(".");
  if (firstDot !== -1) {
    value =
      value.slice(0, firstDot + 1) +
      value.slice(firstDot + 1).replace(/\./g, "");
  }
  return (isNegative ? "-" : "") + value;
};

const trimTrailingZeros = (raw) => {
  const str = String(raw ?? "");
  if (!str.includes(".") || !NUMERIC_RE.test(str)) return str;
  return str.replace(/(\.\d*?[1-9])0+$|\.0*$/, "$1");
};

const normalizeDisplay = (input) => {
  if (document.activeElement === input) return;
  const trimmed = trimTrailingZeros(input.value);
  if (trimmed !== input.value) input.value = trimmed;
};

// Bloque la frappe de caractères invalides (avant qu'ils n'entrent).
const onKeypress = (event) => {
  const key = event.key;
  // laisser passer les touches de contrôle (Enter, Backspace, flèches…)
  if (key?.length !== 1) return;

  const input = event.target;

  if (/\d/.test(key)) return; // chiffre : OK

  // point / virgule : autorisé seulement s'il n'y a pas déjà un point
  if (key === "." || key === ",") {
    if (input.value.includes(".")) event.preventDefault();
    return;
  }

  // tiret : seulement en première position et s'il n'y en a pas déjà
  if (key === "-") {
    if (input.selectionStart !== 0 || input.value.includes("-")) {
      event.preventDefault();
    }
    return;
  }

  // tout le reste est interdit
  event.preventDefault();
};

// Nettoie la valeur (collage, virgule, état incohérent) et mémorise la saisie.
// Écouté en phase de capture : le champ doit être assaini avant que le composant
// ne lise sa valeur.
const onInput = (event) => {
  const input = event.target;
  const clean = sanitize(input.value);
  input[TYPED_VALUE] = clean;

  if (clean !== input.value) {
    const cursor = input.selectionStart || 0;
    const removed = input.value.length - clean.length;

    input.value = clean;
    // re-notifier Vue / Vuetify avec la valeur nettoyée
    input.dispatchEvent(new Event("input", { bubbles: true }));

    // tenter de conserver la position du curseur
    if (SELECTABLE_TYPES.has(input.type)) {
      const pos = Math.max(0, cursor - removed);
      input.setSelectionRange(pos, pos);
    }
  }

  input[TYPED_CARET] = input.selectionStart;
};

export default {
  mounted(el) {
    const input = getInput(el);
    if (!input) return;
    input.type = "text";
    input.inputMode = "decimal";
    input._vModifiers = { ...input._vModifiers, number: true };
    el[KEYPRESS_HANDLER] = onKeypress;
    el[INPUT_HANDLER] = onInput;
    input.addEventListener("keypress", onKeypress);
    input.addEventListener("input", onInput, true);
    normalizeDisplay(input);
  },
  // `v-model.number` fait passer la valeur émise par looseToNumber : "0," devient 0,
  // que Vue réécrit ensuite dans le champ — le séparateur disparaît dès sa frappe.
  // On restitue la saisie tant qu'elle désigne le même nombre.
  updated(el) {
    const input = getInput(el);
    if (!input) return;
    normalizeDisplay(input);
    if (document.activeElement !== input) return;

    const typed = input[TYPED_VALUE];
    if (typed == null || typed === input.value) return;
    if (Number(typed) !== Number(input.value)) return;

    const caret = input[TYPED_CARET] ?? typed.length;
    input.value = typed;
    if (SELECTABLE_TYPES.has(input.type)) input.setSelectionRange(caret, caret);
  },
  unmounted(el) {
    const input = getInput(el);
    if (!input) return;
    if (el[KEYPRESS_HANDLER]) {
      input.removeEventListener("keypress", el[KEYPRESS_HANDLER]);
      delete el[KEYPRESS_HANDLER];
    }
    if (el[INPUT_HANDLER]) {
      input.removeEventListener("input", el[INPUT_HANDLER], true);
      delete el[INPUT_HANDLER];
    }
  },
};
